import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ShieldCheck, Package, Truck } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { BRANDS, CONTACT } from "../data/products";

const MARQUEE_ITEMS = ["DRYWALL SCREWS", "TIGERSTAR", "GRIPSTAR", "GREY PATTA", "FASTENERS", "WHOLESALE ONLY", "WEST BENGAL, INDIA"];

const CHAPTERS = [
    {
        no: "01",
        icon: ShieldCheck,
        title: "Quality you can torque down",
        text: "High-strength, rust-resistant screws with precision threads — every carton checked before it leaves our godown.",
    },
    {
        no: "02",
        icon: Package,
        title: "Packing built for trade",
        text: "Standardised cartons — 20 boxes for screws, 36 pcs for tape, 24 for Grey Patta — so counting stock is never a guess.",
    },
    {
        no: "03",
        icon: Truck,
        title: "Wholesale, without the runaround",
        text: "One call or one WhatsApp message. Straight talk on availability, bulk pricing and dispatch across India.",
    },
];

const STATS = [
    ["10+", "Years in Business"],
    ["8+", "States Supplied Across India"],
    ["1000+", "Happy Dealers"],
    ["4", "In-House Brands"],
];

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

    useEffect(() => {
        document.title = "Mamta Enterprises — India's Trusted Wholesale Supplier";
    }, []);

    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden blueprint-grid spotlight pt-24 pb-16" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 relative z-10">
                        <MaskedLine delay={0.15}>
                            <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-muted-foreground border border-border bg-white px-4 py-2" data-testid="hero-overline">
                                <span className="w-2 h-2 bg-brand inline-block" />
                                Wholesale Catalogue — {CONTACT.location}
                            </span>
                        </MaskedLine>
                        <h1 className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-ink mt-6 text-5xl sm:text-7xl lg:text-8xl">
                            <MaskedLine delay={0.3}>India's Trusted</MaskedLine>
                            <MaskedLine delay={0.45}>
                                <span className="text-outline">Wholesale</span>
                            </MaskedLine>
                            <MaskedLine delay={0.6}>
                                <span className="text-brand">Supplier</span>
                            </MaskedLine>
                        </h1>
                        <MaskedLine delay={0.8} className="mt-6">
                            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-intro">
                                Mamta Enterprises supplies drywall screws, fasteners and drywall accessories in bulk to contractors, retailers and distributors across India. No retail. No middlemen. Just cartons, on time.
                            </p>
                        </MaskedLine>
                        <motion.div
                            className="mt-10 flex flex-col sm:flex-row gap-4"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Link
                                to="/contact"
                                data-testid="hero-contact-cta"
                                className="group inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-base px-8 py-4 transition-colors duration-200"
                            >
                                Contact Us
                                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/products"
                                data-testid="hero-products-cta"
                                className="group inline-flex items-center justify-center gap-3 border-2 border-ink text-ink hover:bg-ink hover:text-white font-display uppercase tracking-[0.15em] text-base px-8 py-4 transition-colors duration-200"
                            >
                                Browse Catalogue
                                <ArrowUpRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5 relative hidden sm:block">
                        <motion.div
                            style={{ y: imgY, rotate: imgRotate }}
                            initial={{ opacity: 0, scale: 0.92, y: 40 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 border border-brand/30 translate-x-4 translate-y-4 pointer-events-none" />
                            <div className="bg-white border border-border p-6 shadow-[0_30px_60px_-20px_rgba(26,26,26,0.25)]" data-testid="hero-product-image-frame">
                                <img src="/assets/hero-built.png" alt="Mamta Enterprises brands on a project site — Built For Every Project" className="w-full h-auto object-cover" data-testid="hero-product-image" />
                            </div>
                            <div className="absolute -bottom-5 -left-5 bg-ink text-white px-5 py-3 font-display uppercase tracking-[0.2em] text-xs" data-testid="hero-image-badge">
                                All 4 Brands — In Stock
                            </div>
                        </motion.div>
                    </div>
                </div>

                <motion.div
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-muted-foreground"
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                    <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                    <span className="w-px h-8 bg-ink/30" />
                </motion.div>
            </section>

            {/* MARQUEE */}
            <div className="bg-ink text-white py-5 border-y-4 border-brand" data-testid="home-marquee">
                <Marquee speed={30} gradient={false}>
                    {MARQUEE_ITEMS.map((item) => (
                        <span key={item} className="flex items-center">
                            <span className="font-display uppercase tracking-[0.25em] text-lg sm:text-2xl px-8">{item}</span>
                            <span className="w-3 h-3 bg-brand inline-block rotate-45" />
                        </span>
                    ))}
                </Marquee>
            </div>

            {/* STATS */}
            <section className="border-b border-border bg-white" data-testid="home-stats">
                <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
                    {STATS.map(([num, label], i) => (
                        <Reveal key={label} delay={i * 0.08} className={`px-6 py-10 sm:py-14 text-center ${i !== 0 ? "border-l border-border" : ""} ${i > 1 ? "border-t lg:border-t-0 border-border" : ""}`}>
                            <div className="font-display font-semibold text-4xl sm:text-6xl text-brand" data-testid={`stat-number-${i}`}>
                                {num}
                            </div>
                            <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-ink font-medium mt-2">{label}</div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PREMIUM SPOTLIGHT — GREY PATTA */}
            <section className="bg-brand text-white blueprint-grid-dark border-y-4 border-ink overflow-hidden" data-testid="premium-spotlight-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <Reveal>
                            <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-white border border-white/50 px-4 py-2" data-testid="premium-spotlight-overline">
                                <span className="w-2 h-2 bg-white inline-block rotate-45" />
                                Premium Quality — Featured Brand
                            </span>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-display font-semibold uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6" data-testid="premium-spotlight-title">
                                Grey<br /><span className="text-outline-white">Patta.</span>
                            </h2>
                            <p className="text-white/85 mt-5 max-w-lg text-base sm:text-lg leading-relaxed" data-testid="premium-spotlight-text">
                                Our best-selling premium drywall screws — grey phosphate coated for superior rust resistance, with a sharp point, deep thread and a hold that stays strong.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="flex flex-wrap gap-2 mt-7" data-testid="premium-spotlight-chips">
                                {["Grey Phosphate Coated", "Sharp Point", "Deep Thread", "Strong Hold", "1 Carton = 24 Boxes"].map((chip) => (
                                    <span key={chip} className="border border-white/40 text-white px-3.5 py-1.5 text-xs uppercase tracking-[0.15em]">
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-9 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact?product=Grey Patta"
                                    data-testid="premium-spotlight-enquire"
                                    className="group inline-flex items-center justify-center gap-3 bg-white text-brand hover:bg-ink hover:text-white font-display uppercase tracking-[0.15em] text-sm px-8 py-4 transition-colors"
                                >
                                    Enquire Now
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/products#greypatta"
                                    data-testid="premium-spotlight-details"
                                    className="group inline-flex items-center justify-center gap-3 border-2 border-white text-white hover:bg-white hover:text-brand font-display uppercase tracking-[0.15em] text-sm px-8 py-4 transition-colors"
                                >
                                    View Specs
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="relative" style={{ perspective: 1000 }}>
                            <div className="absolute -inset-3 border border-white/40 -translate-x-3 translate-y-3 pointer-events-none" />
                            <motion.div
                                className="relative bg-white border border-white/20 p-3 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]"
                                whileHover={{ rotateX: 3, rotateY: -4 }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                            >
                                <img
                                    src="/assets/greypatta.jpg"
                                    alt="Grey Patta premium drywall screws box"
                                    className="w-full aspect-[4/3] object-cover object-center"
                                    data-testid="premium-spotlight-image"
                                />
                                <div className="absolute top-6 left-6 bg-brand text-white font-display px-4 py-2 text-xs uppercase tracking-[0.2em]" data-testid="premium-spotlight-badge">
                                    Premium Quality
                                </div>
                            </motion.div>
                            <div className="absolute -bottom-5 right-6 bg-white text-ink px-5 py-3 font-display uppercase tracking-[0.2em] text-xs border-l-4 border-brand" data-testid="premium-spotlight-packing">
                                1" Screws · 24 Boxes / Carton
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BRANDS PREVIEW */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-brands-section">
                <Reveal>
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
                        <div>
                            <span className="text-xs uppercase tracking-[0.3em] text-brand" data-testid="home-brands-overline">The Range</span>
                            <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ink mt-2">
                                Four brands.<br />One standard.
                            </h2>
                        </div>
                        <Link to="/products" data-testid="home-brands-viewall" className="group inline-flex items-center gap-2 font-display uppercase tracking-[0.15em] text-sm text-ink hover:text-brand transition-colors">
                            Full catalogue <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
                    {BRANDS.map((brand, i) => (
                        <Reveal key={brand.id} delay={i * 0.1} className="bg-white">
                            <Link to={`/products#${brand.id}`} data-testid={`home-brand-card-${brand.id}`} className="group block p-6 h-full">
                                <div className="flex items-start justify-between mb-4">
                                    <span className="font-display text-sm text-muted-foreground">{brand.index}</span>
                                    <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </div>
                                <div className="overflow-hidden border border-border mb-5 relative" style={{ perspective: 800 }}>
                                    <motion.img
                                        src={brand.image}
                                        alt={`${brand.name} product box`}
                                        className="w-full h-48 object-cover"
                                        whileHover={{ scale: 1.06, rotateX: 3, rotateY: -3 }}
                                        transition={{ duration: 0.4, ease: "easeOut" }}
                                    />
                                    {brand.premium && (
                                        <span className="absolute top-2 left-2 bg-brand text-white text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1" data-testid="home-premium-badge-greypatta">
                                            Premium Quality
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-display font-semibold uppercase tracking-wide text-2xl text-ink group-hover:text-brand transition-colors">{brand.name}</h3>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{brand.tagline}</p>
                                <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-brand">{brand.accent}</div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CHAPTERS */}
            <section className="bg-white border-y border-border" data-testid="home-chapters-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.3em] text-brand">Why Mamta</span>
                        <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl text-ink mt-2 mb-14">The wholesale manifesto</h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
                        {CHAPTERS.map((ch, i) => (
                            <Reveal key={ch.no} delay={i * 0.12} className="bg-white">
                                <div className="p-8 sm:p-10 h-full group hover:bg-brand-light/40 transition-colors duration-300" data-testid={`home-chapter-${ch.no}`}>
                                    <div className="flex items-center justify-between mb-8">
                                        <span className="font-display font-semibold text-5xl text-outline-dark group-hover:text-brand group-hover:[-webkit-text-stroke:0px] transition-all">{ch.no}</span>
                                        <ch.icon className="w-7 h-7 text-brand" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-display font-medium uppercase tracking-wide text-xl text-ink mb-3">{ch.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{ch.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA BANNER */}
            <section className="bg-ink text-white blueprint-grid-dark" data-testid="home-cta-banner">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
                    <Reveal>
                        <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-6xl leading-[1.02]">
                            Ready to stock up?<br />
                            <span className="text-brand">Let's talk cartons.</span>
                        </h2>
                        <p className="text-white/60 mt-4 max-w-lg text-base leading-relaxed">
                            Send us an enquiry with the products and quantities you need — we reply with pricing and availability.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/contact"
                            data-testid="home-cta-contact-button"
                            className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-base px-10 py-5 transition-colors"
                        >
                            Contact Us
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default Home;
